import { useCallback } from 'react';

import { type Clip, ClipType } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { ClipUtils, isPdfRenderedExtension } from '@air/utils-clips';

import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { useAddToCanvasModal } from '~/components/Modals/AddToCanvasModal/useAddToCanvasModal';
import { DeletePrivateClipsModal } from '~/components/Modals/DeletePrivateClipsModal';
import { MoveItemsModal } from '~/components/Modals/MoveItemsModal/MoveItemsModal';
import { useRemoveAssetsFromBoardModal } from '~/components/Modals/RemoveClipsFromBoardModal/hooks/useRemoveAssetsFromBoardModal';
import { RenameClipsModal } from '~/components/Modals/RenameClipsModal';
import { SetBoardThumbnailModal } from '~/components/Modals/SetBoardThumbnail/SetBoardThumbnailModal';
import { BoardCoverSelectModal } from '~/components/v2/components/board/board-cover/BoardCoverSelectModal';
import { PrivateContextSourcesAddClipModal } from '~/components/v2/components/context-sources/modals/PrivateContextSourcesAddClipModal';
import { useCanEditContextSources } from '~/components/v2/hooks/useCanEditContextSources';
import { type GetItemMenuOptionsCallback } from '~/constants/MenuOptions';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useAddToCollectionOption } from '~/hooks/useAddToCollectionOption';
import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCanShowInFinderOption } from '~/hooks/useCanShowInFinderOption';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useGetPrivateClipMenuOptions } from '~/hooks/useGetPrivateClipMenuOptions';
import { useGoToAssetPage } from '~/hooks/useGoToAssetPage';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useDesktopAppConnectionContext } from '~/providers/DesktopAppConnectionProvider';
import { CentralizedBoardSelectors, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canChangeVersionDetails,
  canDeleteAsset,
  canDownloadVersion,
  canMoveAsset,
} from '~/utils/permissions/assetPermissions';
import { canRemoveAssetsFromBoard } from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export function useGetPrivateAssetMenuOptionsCallback() {
  const store = useAirStore();
  const { goToAssetPage } = useGoToAssetPage();
  const { canShowOpenInFinder } = useCanShowInFinderOption();
  const { isFlattenedView } = useIsFlattenedView();

  const { openClipInFinder } = useDesktopAppConnectionContext();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { getAssetPermissions } = useAssetPermissionsCache();
  const { canEditContextSources } = useCanEditContextSources();

  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const getAddToCollectionOption = useAddToCollectionOption();
  const { getClipMenuOptions } = useGetPrivateClipMenuOptions();

  const [showRenameClipsModal] = useAirModal(RenameClipsModal);
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { showRemoveAssetsFromBoardModal } = useRemoveAssetsFromBoardModal();
  const [showDeletePrivateClipsModal] = useAirModal(DeletePrivateClipsModal);
  const [showMoveToBoardModal] = useAirModal(MoveItemsModal);
  const [showSetBoardThumbnailModal] = useAirModal(SetBoardThumbnailModal);
  const [showBoardCoverSelectModal] = useAirModal(BoardCoverSelectModal);
  const [showAddContextSourceClipModal] = useAirModal(PrivateContextSourcesAddClipModal);
  const { showAddToCanvasModal } = useAddToCanvasModal();

  const getAssetMenuOptionsCallback: GetItemMenuOptionsCallback<Clip> = useCallback(
    ({ item: clip }) =>
      ({ trackLocation }) => {
        const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
        const currentBoardTitle = centralizedBoardTitleSelector(store.getState());
        const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;

        const clipPermissions = getClipPermissions(clip.id);
        const assetPermissions = getAssetPermissions(clip.assetId);

        const canDownloadClip = canDownloadVersion(clipPermissions);
        const canRemoveAssets = currentBoardId && canRemoveAssetsFromBoard(currentBoardPermissions);
        const canRenameClip = canChangeVersionDetails(clipPermissions);
        const canMove = canMoveAsset(assetPermissions);

        const canDelete = canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions);
        const canAddToBoard = canAddAssetToBoard(assetPermissions);
        const canManageApplications = canChangeAssetCustomFields(assetPermissions);

        if (!clipPermissions) {
          return [];
        }

        return getClipMenuOptions({
          customFieldsMenuOptions: canManageApplications ? getCustomFieldsMenuOptions({ assets: [clip] }) : undefined,
          addToCollectionOption: getAddToCollectionOption
            ? getAddToCollectionOption({ assetIds: [clip.assetId], boardIds: [] })
            : undefined,
          onAddToBoard: canAddToBoard
            ? () =>
                showAddAssetsToBoardsModal({
                  clipIds: [clip.id],
                  trackLocation,
                  parentBoardId: currentBoardId,
                })
            : undefined,
          onAddToCanvas: () => showAddToCanvasModal({ clips: [clip] }),
          onAddToContext: canEditContextSources
            ? () => {
                showAddContextSourceClipModal({
                  clip,
                });
              }
            : undefined,
          onDelete: canDelete
            ? () =>
                showDeletePrivateClipsModal({
                  assetIds: [clip.assetId],
                  trackLocation,
                  parentBoardId: currentBoardId,
                })
            : undefined,
          onMoveToBoard:
            currentBoardId && canMove
              ? () =>
                  showMoveToBoardModal({
                    boards: [],
                    clips: [
                      {
                        id: clip.id,
                        assetId: clip.assetId,
                        openCommentCount: clip.openCommentCount,
                      },
                    ],
                    trackLocation,
                  })
              : undefined,
          onOpen: () =>
            goToAssetPage({
              asset: clip,
              trackLocation: 'open-dropdown-select',
            }),
          onOpenOnDesktop:
            /**
             * @see https://air-labs-team.slack.com/archives/C052RDYCGAG/p1696874696775379
             */
            canShowOpenInFinder && canDownloadClip
              ? () => {
                  openClipInFinder(clip, currentBoardId);
                }
              : undefined,
          onRemoveFromCurrentBoard:
            !isFlattenedView && currentBoardId && currentBoardTitle && canRemoveAssets
              ? () => {
                  showRemoveAssetsFromBoardModal({
                    boardId: currentBoardId,
                    boardTitle: currentBoardTitle,
                    clipIds: [clip.id],
                    trackLocation,
                  });
                }
              : undefined,
          onRenameAsset: canRenameClip
            ? () =>
                showRenameClipsModal({
                  clip,
                })
            : undefined,
          onSetBoardCover:
            clip.type === ClipType.photo && !isPdfRenderedExtension(clip.ext)
              ? () => {
                  showBoardCoverSelectModal({
                    clip,
                    trackLocation,
                  });
                }
              : undefined,
          onSetBoardThumbnail: ClipUtils.canHavePreviewImage(clip.type)
            ? () =>
                showSetBoardThumbnailModal({
                  trackLocation: 'asset-details',
                  clip,
                })
            : undefined,
        });
      },
    [
      store,
      getBoardPermissions,
      getClipPermissions,
      getAssetPermissions,
      workspacePermissions,
      getClipMenuOptions,
      getCustomFieldsMenuOptions,
      getAddToCollectionOption,
      canShowOpenInFinder,
      isFlattenedView,
      showAddAssetsToBoardsModal,
      showDeletePrivateClipsModal,
      showMoveToBoardModal,
      goToAssetPage,
      openClipInFinder,
      showRemoveAssetsFromBoardModal,
      showRenameClipsModal,
      showSetBoardThumbnailModal,
      showBoardCoverSelectModal,
      canEditContextSources,
      showAddContextSourceClipModal,
      showAddToCanvasModal,
    ],
  );

  return {
    getAssetMenuOptionsCallback,
  };
}
